import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { StatusToast } from '@components/CustomToast/StatusToast.web';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { InviteMemberModal } from '@components/Profile/TeamDetailPage/InviteMemberModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { CollapsableSectionListV3 } from '@components/shared/CollapsableSectionListV3.web';
import Icon from '@components/shared/Icon/RestyleIcon';
import {
  MemberRowSkeleton,
  PendingInvitationRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { TeamNoMembersBanner } from '@components/Team/TeamNoMembersBanner';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import { PopupHiddenSkills } from '@components/Web/PopupHiddenSkills.web';
import {
  GetTeamDetailDocument,
  ListPendingUserRequestsDocument,
  Team,
  TeamRole,
  User,
  useCreateChatMutation,
  useDeleteTeamInvitationMutation,
  useResendTeamInvitationMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMembersMutation,
  useListPendingUserRequestsQuery,
  UserRequestCategory,
  UserRequestable,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useApolloClient } from '@hooks/useApolloClient';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { useRefetchOnFocus } from '@hooks/useRefetchOnFocus';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { formatPhoneNumber } from '@utils/formatters/phone';
import { getTeamRoleEnumValue, TEAM_ROLE_DESCRIPTIONS } from '@utils/teams';
import { shouldShowPaywall } from '@utils/teams/paywall';

import { ChatIconWithTooltip } from './ChatIconWithTooltip.web';
import { computeShowNoMembersBanner } from './computeShowNoMembersBanner';
import { isDisplayablePendingInvitation } from './isDisplayablePendingInvitation';
import { isPendingSectionInitialLoading } from './isPendingSectionInitialLoading';
import { MembersFilterOverlay } from './MembersFilterOverlay.web';
import { MembersSectionHeader } from './MembersSectionHeader.web';
import { MembersTableHeader } from './MembersTableHeader.web';
import { MessageMemberOverlay } from './MessageMemberOverlay.web';
import { PendingInvitationsTableHeader } from './PendingInvitationsTableHeader.web';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.grey05,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.grey05,
    width: 132,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing.xs,
  },
}));

type MemberGroup = 'ACTIVE' | 'PENDING' | 'INVITED' | 'REJECTED';
type Header = { id: MemberGroup; name: string; count: number };
type SectionData = {
  sectionHeader: Header;
  data: User[];
};
export type UserSectionNew = {
  sectionData: SectionData;
  loadMore?: () => void;
  members?: [];
};

// Pending invitation type (from team.invitations or listPendingUserRequests)
type PendingInvitation = {
  id: string;
  email: string;
  name?: string;
  invitedBy: string;
  dateSent: Date;
  expirationDate: Date;
  role: TeamRole;
  /** Set when item is from listPendingUserRequests (pending request); use for delete/resend API */
  userRequestId?: string;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    avatar?: {
      id: string;
      isPreviewable?: boolean;
    } | null;
  } | null;
};

// Role selector component for dropdown
const TeamMemberRoleSelector = ({
  onRoleSelect,
}: {
  onRoleSelect: (role: TeamRole) => void;
}) => {
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const roleOptions = [
    {
      title: t('shared:roleAdmin'),
      description: TEAM_ROLE_DESCRIPTIONS[TeamRole.Admin],
      id: TeamRole.Admin,
    },
    {
      title: t('models:profile.subscriptionPlans.member'),
      description: TEAM_ROLE_DESCRIPTIONS[TeamRole.Member],
      id: TeamRole.Member,
    },
  ];

  return (
    <Box
      width={320}
      py='xxs'
      style={{
        borderRadius: theme.borderRadii.xs,
        borderWidth: 1,
        borderColor: theme.colors.grey02,
      }}>
      {roleOptions.map(({ title, description, id }) => {
        const itemId = String(id);
        const isHovered = hoveredItem === itemId;
        return (
          <Box key={itemId}>
            <TouchableOpacity
              onPress={() => onRoleSelect(id)}
              // @ts-ignore - web-specific props
              onMouseEnter={() => setHoveredItem(itemId)}
              onMouseLeave={() => setHoveredItem(null)}>
              <Box
                px='xs'
                py='xs'
                flex={1}
                flexDirection='column'
                backgroundColor={isHovered ? 'grey01' : 'white'}>
                <Text
                  ml='xs'
                  accessibilityLabel={title}
                  variant='webBodySecondary'
                  color='textPrimary'
                  numberOfLines={1}>
                  {title}
                </Text>
                {!!description && (
                  <Text
                    ml='xs'
                    marginTop='xxxs'
                    variant='webBodyHintSubline'
                    color='grey04'
                    numberOfLines={2}>
                    {description}
                  </Text>
                )}
              </Box>
            </TouchableOpacity>
          </Box>
        );
      })}
    </Box>
  );
};

// Member/pending rows keep their own local hover state (TA-9005): the row
// tints to grey01 on hover; controls inside deepen one step to grey02. The
// rows are rendered by useCallback render functions, so hover lives in this
// wrapper component rather than in the (hook-less) render callbacks.
const HoverableMemberRow: React.FC<{
  isTheLastOne: boolean;
  children: React.ReactNode;
}> = ({ isTheLastOne, children }) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <Box
      {...hoverProps}
      flexDirection='row'
      backgroundColor={isHovered ? 'grey01' : 'white'}
      paddingVertical='xs'
      paddingHorizontal='m'
      borderBottomWidth={isTheLastOne ? 0 : 1}
      borderBottomColor='grey02'
      alignItems='center'>
      {children}
    </Box>
  );
};

const INITIAL_EXPANDED_SECTION_IDS = ['ACTIVE', 'PENDING'];
const ListHeaderComponent = () => <Box marginTop='none' />;
const renderNewTaskItem = () => null;
const noopRightMenuAction = async () => {
  // No-op for members list
};
const teamMemberFilterOptions = ['admin', 'member'] as const;

export const MembersTab = () => {
  const { selectedTeam: team, setSelectedTeam } = useWebDrawerNavigator();
  const { client } = useApolloClient();
  const { me } = useMe();
  const { t } = useTranslation('models');
  const toast = useToast();
  const [isSortName, setSortName] = useState(false);
  const [isSortNameAsc, setSortNameAsc] = useState(false);
  const [isSortPendingDate, setSortPendingDate] = useState(false);
  const [isSortPendingDateAsc, setSortPendingDateAsc] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    userId: string;
    role: TeamRole;
  } | null>(null);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [pendingMemberToRemove, setPendingMemberToRemove] =
    useState<User | null>(null);
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const [showMessageMemberOverlay, setShowMessageMemberOverlay] =
    useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterRole, setFilterRole] = useState<'admin' | 'member' | null>(null);
  const [messageMemberSearch, setMessageMemberSearch] = useState('');
  const [selectedMembersForChat, setSelectedMembersForChat] = useState<
    string[]
  >([]);
  const [pendingInvitationToDelete, setPendingInvitationToDelete] =
    useState<PendingInvitation | null>(null);
  const [showDeleteInvitationModal, setShowDeleteInvitationModal] =
    useState(false);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const messageMemberButtonRef = useRef<HTMLDivElement>(null);
  const filterOverlayRef = useRef<HTMLDivElement>(null);
  const messageMemberOverlayRef = useRef<HTMLDivElement>(null);
  const navigation = useAppNavigation();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);

  const {
    chatListOpen,
    chatExpanded,
    setChatListOpen,
    setShouldChatListOpen,
    setPaywallModalOpen,
    setPaywallTeam,
  } = useWebDrawer();

  const {
    data: pendingRequest,
    loading: pendingRequestLoading,
    refetch: refetchPendingRequest,
  } = useListPendingUserRequestsQuery({
    variables: {
      attributes: {
        requestType: UserRequestCategory.MemberAddition,
        userRequestableId: team?.id ?? '',
        userRequestableType: UserRequestable.Account,
      },
    },
    skip: !team?.id,
    fetchPolicy: 'cache-and-network',
  });
  // stalenessMs=0: pending requests gate accept/decline controls, so a
  // refocus (including tab-switch-back within this top-tab navigator)
  // should always revalidate rather than risk a stale cross-account read.
  // Reject (not resolve) when skipped so the hook's "clock only advances
  // on success" guarantee holds — a skipped cycle must not be mistaken for
  // a real refetch on the next refocus.
  useRefetchOnFocus(
    useCallback(
      () =>
        team?.id
          ? refetchPendingRequest()
          : Promise.reject(new Error('skipped: no team id')),
      [team?.id, refetchPendingRequest]
    ),
    0
  );
  const pendingRequestInitialLoading = isPendingSectionInitialLoading(
    pendingRequestLoading,
    pendingRequest
  );

  // getPublicTeam, not getTeam (TA-10850): this tab renders inside
  // TeamDetailPage, which can be open for a viewer who is not a member yet —
  // getTeam answers "The requested team could not be found" for them. Same
  // variables as the parent so both share one cache entry.
  const { loading: teamLoading } = useTeamDetailFromQuery({
    teamUUId: team?.uuid,
    fetchPolicy: 'network-only',
    includePending: true,
  });

  const { setIsOpenChat, setActiveMessageId } = useActiveChat();

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    const { createChat: chat } = createChatData;
    // open the chat drawer
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    // setActiveChat(chat);
    setActiveMessageId?.(undefined);
    navigation.navigateToChatDrawer({
      screen: ChatDrawerScreen.details,
      activeChat: chat,
    });
  }, [createChatData]);

  const [
    resendTeamInvitation,
    { data: resendTeamInvitationData, error: resendTeamInvitationError },
  ] = useResendTeamInvitationMutation({
    refetchQueries: team?.uuid
      ? [
          {
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
          },
          ...(team?.id
            ? [
                {
                  query: ListPendingUserRequestsDocument,
                  variables: {
                    attributes: {
                      requestType: UserRequestCategory.MemberAddition,
                      userRequestableId: team.id,
                      userRequestableType: UserRequestable.Account,
                    },
                  },
                },
              ]
            : []),
        ]
      : [],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!resendTeamInvitationData) return;
    toast.show(
      <StatusToast
        message={t('projects.members.invitationResent')}
        duration={3000}
        type='success'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [resendTeamInvitationData]);

  useEffect(() => {
    if (!resendTeamInvitationError) return;
    toast.show(
      <StatusToast
        message={t('projects.members.resendFailedTryAgain')}
        duration={3000}
        type='error'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [resendTeamInvitationError]);

  const [
    deleteTeamInvitation,
    {
      loading: isDeletingInvitation,
      data: deleteTeamInvitationData,
      error: deleteTeamInvitationError,
    },
  ] = useDeleteTeamInvitationMutation({
    refetchQueries: team?.uuid
      ? [
          {
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
          },
          ...(team?.id
            ? [
                {
                  query: ListPendingUserRequestsDocument,
                  variables: {
                    attributes: {
                      requestType: UserRequestCategory.MemberAddition,
                      userRequestableId: team.id,
                      userRequestableType: UserRequestable.Account,
                    },
                  },
                },
              ]
            : []),
        ]
      : [],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!deleteTeamInvitationData) return;
    // Close modal immediately for better UX
    setShowDeleteInvitationModal(false);
    setPendingInvitationToDelete(null);
    // Show success toast
    toast.show(
      <StatusToast
        message={t('projects.members.inviteDeleted')}
        duration={3000}
        type='success'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
    // Update context in the background (non-blocking)
    const updateContext = async () => {
      if (team?.uuid && client) {
        try {
          const { data } = await client.query({
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
            fetchPolicy: 'network-only',
          });
          // getTeamDetail resolves getPublicTeam, a TeamOrPublicTeam union. Only
          // the Team branch carries the member fields selectedTeam needs.
          if (data?.getPublicTeam?.__typename === 'Team') {
            setSelectedTeam(data.getPublicTeam as Team);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating team context:', error);
        }
      }
    };
    updateContext();
  }, [deleteTeamInvitationData]);

  useEffect(() => {
    if (!deleteTeamInvitationError) return;
    toast.show(
      <StatusToast
        message={t('shared:deleteFailedTryAgain')}
        duration={3000}
        type='error'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [deleteTeamInvitationError]);

  const [
    updateTeamMembers,
    { data: updateTeamMembersData, error: updateTeamMembersError },
  ] = useUpdateTeamMembersMutation({
    refetchQueries: team?.uuid
      ? [
          {
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
          },
        ]
      : [],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!updateTeamMembersData) return;
    const update = async () => {
      if (team?.uuid && client) {
        try {
          const { data } = await client.query({
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
            fetchPolicy: 'network-only',
          });
          // getTeamDetail resolves getPublicTeam, a TeamOrPublicTeam union. Only
          // the Team branch carries the member fields selectedTeam needs.
          if (data?.getPublicTeam?.__typename === 'Team') {
            setSelectedTeam(data.getPublicTeam as Team);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating team context:', error);
        }
      }
    };
    update();
    toast.show(
      <StatusToast
        message={t('shared:roleUpdated')}
        duration={3000}
        type='success'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [updateTeamMembersData]);

  useEffect(() => {
    if (!updateTeamMembersError) return;
    toast.show(
      <StatusToast
        message={t('shared:updateFailedTryAgain')}
        duration={3000}
        type='error'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [updateTeamMembersError]);

  const [
    removeTeamMember,
    {
      loading: isRemovingMember,
      data: removeTeamMemberData,
      error: removeTeamMemberError,
    },
  ] = useRemoveTeamMemberMutation({
    refetchQueries: team?.uuid
      ? [
          {
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
          },
        ]
      : [],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!removeTeamMemberData) return;
    setShowRemoveConfirmModal(false);
    setPendingMemberToRemove(null);
    toast.show(
      <StatusToast
        message={t('shared:memberDeleted')}
        duration={3000}
        type='success'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
    const updateContext = async () => {
      if (team?.uuid && client) {
        try {
          const { data } = await client.query({
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
            fetchPolicy: 'network-only',
          });
          // getTeamDetail resolves getPublicTeam, a TeamOrPublicTeam union. Only
          // the Team branch carries the member fields selectedTeam needs.
          if (data?.getPublicTeam?.__typename === 'Team') {
            setSelectedTeam(data.getPublicTeam as Team);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating team context:', error);
        }
      }
    };
    updateContext();
  }, [removeTeamMemberData]);

  useEffect(() => {
    if (!removeTeamMemberError) return;
    toast.show(
      <StatusToast
        message={t('shared:deleteFailedTryAgain')}
        duration={3000}
        type='error'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [removeTeamMemberError]);

  const grouped = true;

  // Handle sort toggle - switches between A-Z and Z-A, following ProjectMembers pattern
  const handleSortToggle = useCallback(
    (isAscending: boolean) => {
      if (isSortName && isSortNameAsc === isAscending) {
        // If clicking the same arrow, disable sorting
        setSortName(false);
        setSortNameAsc(false);
      } else {
        // Otherwise, enable sorting with the selected direction
        setSortName(true);
        setSortNameAsc(isAscending);
      }
    },
    [isSortName, isSortNameAsc]
  );

  // Handle pending invitations sort toggle - following ProjectMembers pattern
  const handlePendingSortToggle = useCallback(
    (isAscending: boolean) => {
      if (isSortPendingDate && isSortPendingDateAsc === isAscending) {
        // If clicking the same arrow, disable sorting
        setSortPendingDate(false);
        setSortPendingDateAsc(false);
      } else {
        // Otherwise, enable sorting with the selected direction
        setSortPendingDate(true);
        setSortPendingDateAsc(isAscending);
      }
    },
    [isSortPendingDate, isSortPendingDateAsc]
  );

  // Map team invitations + pending user requests to PendingInvitation format
  const pendingInvitations: PendingInvitation[] = useMemo(() => {
    const fromInvitations: PendingInvitation[] =
      team?.invitations?.map((invitation) => {
        const expirationDate = invitation.expiredAt
          ? new Date(invitation.expiredAt)
          : new Date();
        const defaultInvitationDuration = 7 * 24 * 60 * 60 * 1000;
        const dateSent = invitation.expiredAt
          ? new Date(
              new Date(invitation.expiredAt).getTime() -
                defaultInvitationDuration
            )
          : new Date(Date.now() - defaultInvitationDuration);
        const invitedByName =
          invitation.invitedBy?.name ||
          `${invitation.invitedBy?.firstName || ''} ${
            invitation.invitedBy?.lastName || ''
          }`.trim() ||
          t('shared:unknownUser');
        return {
          id: invitation.id,
          email: invitation.email || '',
          invitedBy: invitedByName,
          dateSent,
          expirationDate,
          role: invitation.role || TeamRole.Member,
          user: invitation.user || null,
        };
      }) ?? [];

    const fromPendingRequests: PendingInvitation[] =
      pendingRequest?.listPendingUserRequests
        ?.filter((request) => (request.invitedBy?.name ?? '').trim() !== '')
        ?.map((request) => {
          const u = request.user;
          const defaultInvitationDuration = 7 * 24 * 60 * 60 * 1000;
          const dateSent = request.createdAt
            ? new Date(request.createdAt)
            : new Date(Date.now());
          const expirationDate = request.expiredAt
            ? new Date(request.expiredAt)
            : new Date(Date.now() + defaultInvitationDuration);
          return {
            id: request.id,
            userRequestId: request.id,
            email: u?.email ?? '',
            invitedBy: request.invitedBy?.name ?? '',
            dateSent: dateSent,
            expirationDate: expirationDate,
            role: request.role as TeamRole,
            user: u
              ? {
                  id: u.id,
                  firstName: u.name ?? null,
                  lastName: null,
                  avatar: u.avatar ?? null,
                }
              : null,
          };
        }) ?? [];

    const activeUserIds = new Set(
      (team?.users as User[] | undefined)?.map((u) => u.id) ?? []
    );

    return [...fromInvitations, ...fromPendingRequests].filter((invitation) =>
      isDisplayablePendingInvitation(invitation, activeUserIds)
    );
  }, [
    team?.invitations,
    team?.users,
    pendingRequest?.listPendingUserRequests,
    t,
  ]);

  const showBanner = computeShowNoMembersBanner({
    bannerDismissed,
    pendingRequestInitialLoading,
    teamLoading,
    team,
    pendingInvitationsCount: pendingInvitations.length,
  });

  // Format date for display using Dayjs
  const formatDate = useCallback((date: Date): string => {
    return dayjs(date).format('MMM D, YYYY');
  }, []);

  // Handle resend invitation (from team.invitations use accountInvitationId; from pending request use userRequestId)
  const handleResendInvitation = useCallback(
    (_invitation: PendingInvitation) => {
      resendTeamInvitation({
        variables: _invitation.userRequestId
          ? { userRequestId: _invitation.userRequestId }
          : { accountInvitationId: _invitation.id },
      });
    },
    [resendTeamInvitation]
  );

  // Handle delete invitation
  const handleDeleteInvitation = useCallback(
    (invitation: PendingInvitation) => {
      setPendingInvitationToDelete(invitation);
      setShowDeleteInvitationModal(true);
    },
    []
  );

  // Handle confirm delete (from team.invitations use accountInvitationId; from pending request use userRequestId)
  const handleConfirmDeleteInvitation = useCallback(() => {
    if (pendingInvitationToDelete) {
      deleteTeamInvitation({
        variables: pendingInvitationToDelete.userRequestId
          ? { userRequestId: pendingInvitationToDelete.userRequestId }
          : { accountInvitationId: pendingInvitationToDelete.id },
      });
    }
  }, [pendingInvitationToDelete, deleteTeamInvitation]);

  // Handle cancel delete invitation
  const handleCancelDeleteInvitation = useCallback(() => {
    setPendingInvitationToDelete(null);
    setShowDeleteInvitationModal(false);
  }, []);

  // Get user's role in the team
  const getUserRole = useCallback(
    (user: User): TeamRole => {
      if (team?.owner?.id === user.id) {
        return TeamRole.Owner;
      }
      if (user.teamRoles?.includes(TeamRole.Admin)) {
        return TeamRole.Admin;
      }
      return TeamRole.Member;
    },
    [team]
  );

  // Get current user's role in the team (prefer API currentUserRole; fallback to deriving from me)
  const getCurrentUserRole = useCallback((): TeamRole => {
    if (team?.currentUserRole != null) {
      return team.currentUserRole as TeamRole;
    }
    if (!me) return TeamRole.Member;
    return getUserRole(me);
  }, [team?.currentUserRole, me, getUserRole]);

  // Check if current user can edit a specific member's role
  const canEditMemberRole = useCallback(
    (member: User): boolean => {
      const currentUserRole = getCurrentUserRole();
      const memberRole = getUserRole(member);

      // Only Owner and Admin can edit roles
      if (
        currentUserRole !== TeamRole.Owner &&
        currentUserRole !== TeamRole.Admin
      ) {
        return false;
      }

      // Cannot edit own role
      if (me?.id === member.id) {
        return false;
      }

      // Admin can edit other Admin and Member, but cannot edit Owner
      if (memberRole === TeamRole.Owner) {
        return false;
      }
      return memberRole === TeamRole.Admin || memberRole === TeamRole.Member;
    },
    [me, getCurrentUserRole, getUserRole]
  );

  // Check if current user can manage invitations
  const canManageInvitations = useCallback((): boolean => {
    const currentUserRole = getCurrentUserRole();
    return (
      currentUserRole === TeamRole.Owner || currentUserRole === TeamRole.Admin
    );
  }, [getCurrentUserRole]);

  const getRoleDisplay = useCallback(
    (role: TeamRole): string => {
      switch (role) {
        case TeamRole.Owner:
          return t('profile.subscriptionPlans.owner');
        case TeamRole.Admin:
          return t('profile.subscriptionPlans.admin');
        default:
          return t('profile.subscriptionPlans.member');
      }
    },
    [t]
  );

  // Render table header based on section
  const renderTableHeader = useCallback(
    (sectionHeader: Header) => {
      // If it's the PENDING section, render pending invitations header.
      // TA-10280: keep the header visible while loading (matching the
      // Active section's header+skeleton pairing) — only hide it once
      // settled with genuinely zero pending invitations.
      const isPendingLoading = pendingRequestInitialLoading || teamLoading;
      if (
        sectionHeader.id === 'PENDING' &&
        !isPendingLoading &&
        pendingInvitations.length === 0
      ) {
        return <></>;
      }
      if (sectionHeader.id === 'PENDING') {
        return (
          <PendingInvitationsTableHeader
            isSortDate={isSortPendingDate}
            isSortDateAsc={isSortPendingDateAsc}
            onSortToggle={handlePendingSortToggle}
            showActionColumn={canManageInvitations()}
          />
        );
      }
      // Otherwise render active members header
      return (
        <MembersTableHeader
          isSortName={isSortName}
          isSortNameAsc={isSortNameAsc}
          onSortToggle={handleSortToggle}
          showDeleteColumn={canManageInvitations()}
        />
      );
    },
    [
      handleSortToggle,
      handlePendingSortToggle,
      isSortName,
      isSortNameAsc,
      isSortPendingDate,
      isSortPendingDateAsc,
      canManageInvitations,
      pendingRequestInitialLoading,
      teamLoading,
      pendingInvitations,
    ]
  );

  const updateTeamMember = useCallback(
    (userId: string, role: TeamRole) => {
      if (!team?.id) return;

      updateTeamMembers({
        variables: {
          teamId: team.id,
          attributes: [
            {
              userId,
              role: getTeamRoleEnumValue(role),
            },
          ],
        },
      });
    },
    [team?.id, updateTeamMembers]
  );

  const handleRoleSelect = useCallback(
    (userId: string, newRole: TeamRole | 'remove') => {
      // Handle remove case separately
      if (newRole === 'remove') {
        // Find the member to remove
        const memberToRemove = team?.users?.find(
          (user) => user.id === userId
        ) as User | undefined;
        if (memberToRemove) {
          setPendingMemberToRemove(memberToRemove);
          setShowRemoveConfirmModal(true);
        }
        return;
      }

      // If changing to Admin, show confirmation modal
      if (newRole === TeamRole.Admin) {
        setPendingRoleChange({ userId, role: newRole });
        setShowConfirmModal(true);
      } else {
        // For other role changes, directly update
        updateTeamMember(userId, newRole);
      }
    },
    [updateTeamMember, team?.users]
  );

  const handleConfirmRoleChange = useCallback(() => {
    if (pendingRoleChange) {
      updateTeamMember(pendingRoleChange.userId, pendingRoleChange.role);
      setPendingRoleChange(null);
    }
    setShowConfirmModal(false);
  }, [pendingRoleChange, updateTeamMember]);

  const handleCancelRoleChange = useCallback(() => {
    setPendingRoleChange(null);
    setShowConfirmModal(false);
  }, []);

  const handleConfirmRemoveMember = useCallback(() => {
    if (pendingMemberToRemove && team?.id) {
      removeTeamMember({
        variables: {
          id: team.id,
          userId: pendingMemberToRemove.id,
        },
      });
    }
  }, [pendingMemberToRemove, team?.id, removeTeamMember]);

  const handleCancelRemoveMember = useCallback(() => {
    setPendingMemberToRemove(null);
    setShowRemoveConfirmModal(false);
  }, []);

  // Render pending invitation item
  const renderPendingInvitationItem = useCallback(
    (item: PendingInvitation, isTheLastOne: boolean) => {
      const roleDisplay = getRoleDisplay(item.role);
      const isExpired = dayjs(item.expirationDate).isBefore(dayjs());
      return (
        <HoverableMemberRow isTheLastOne={isTheLastOne}>
          {/* Email or Name Column */}
          <Box flex={2.66} flexDirection='row' alignItems='center' minWidth={0}>
            {item.user ? (
              <>
                <Box
                  onStartShouldSetResponder={() => true}
                  onResponderTerminationRequest={() => false}>
                  {/* @ts-ignore - Avatar component accepts partial File type with id */}
                  <Avatar
                    id={item.user.id}
                    avatar={item.user.avatar as any}
                    label={
                      `${item.user.firstName || ''} ${
                        item.user.lastName || ''
                      }`.trim() || item.email
                    }
                    size='small'
                    disableAvatarPress={false}
                  />
                </Box>
                <Box flex={1} minWidth={0} marginLeft='xs'>
                  <Text
                    variant='webBodySecondary'
                    color='textPrimary'
                    ellipsizeMode='tail'
                    numberOfLines={1}
                    style={{ overflow: 'hidden' }}>
                    {`${item.user.firstName || ''} ${
                      item.user.lastName || ''
                    }`.trim() || item.email}
                  </Text>
                </Box>
              </>
            ) : (
              <Box
                flexDirection='row'
                alignItems='center'
                flex={1}
                minWidth={0}>
                <Box
                  width={32}
                  height={32}
                  backgroundColor='grey02'
                  borderRadius='xxl'
                  justifyContent='center'
                  alignItems='center'
                  marginRight='xs'>
                  <Icon name='Mail' variant='s' color='grey04' />
                </Box>
                <Box flex={1} minWidth={0}>
                  <Text
                    variant='webBodySecondary'
                    color='textSecondary'
                    ellipsizeMode='tail'
                    numberOfLines={1}
                    style={{ overflow: 'hidden' }}>
                    {item.email}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>

          {/* Invited By Column */}
          <Box flex={2.4} alignItems='flex-start' justifyContent='center'>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              numberOfLines={1}
              ml='m'>
              {item.invitedBy}
            </Text>
          </Box>

          {/* Date Sent Column */}
          <Box flex={1.64} alignItems='flex-start' justifyContent='center'>
            <Text variant='webBodySecondary' color='textSecondary' ml='m'>
              {formatDate(item.dateSent)}
            </Text>
          </Box>

          {/* Expiration Date Column */}
          <Box flex={1.64} alignItems='flex-start' justifyContent='center'>
            <Text
              variant='webBodySecondary'
              color={isExpired ? 'alertRed' : 'textSecondary'}
              ml='m'>
              {formatDate(item.expirationDate)}
            </Text>
          </Box>

          {/* Role Column */}
          <Box flex={1.2} alignItems='flex-start' justifyContent='center'>
            <Text variant='webBodySecondary' color='textSecondary' ml='m'>
              {roleDisplay}
            </Text>
          </Box>

          {/* Action Column - Only visible to Owner and Admin */}
          {canManageInvitations() && (
            <Box
              flex={1.28}
              flexDirection='row'
              alignItems='center'
              justifyContent='flex-end'>
              <Box
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                flex={1}>
                <TooltipWebV3
                  title={
                    <Text ml='xxs' variant='labelSmall' color='white'>
                      {t('projects.members.resendInvitation')}
                    </Text>
                  }
                  placement='bottom-end'
                  variant='dark'
                  marginOffset={8}>
                  <SimpleHoverable
                    onPress={() => handleResendInvitation(item)}
                    accessibilityLabel={t('shared:resendInvitationTo', {
                      email: item.email,
                    })}>
                    {(isHovered) => (
                      <Box
                        p='xs'
                        borderRadius='xs'
                        backgroundColor={isHovered ? 'grey02' : 'transparent'}>
                        <Icon
                          name='RefreshCw'
                          variant='m'
                          color='textSecondary'
                        />
                      </Box>
                    )}
                  </SimpleHoverable>
                </TooltipWebV3>
              </Box>
              <Box
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                flex={1}>
                <TooltipWebV3
                  title={
                    <Text ml='xxs' variant='labelSmall' color='white'>
                      {t('projects.members.removeInvitation')}
                    </Text>
                  }
                  placement='bottom-end'
                  variant='dark'
                  marginOffset={8}>
                  <SimpleHoverable
                    onPress={() => handleDeleteInvitation(item)}
                    accessibilityLabel={t('shared:cancelInvitationTo', {
                      email: item.email,
                    })}>
                    {(isHovered) => (
                      <Box
                        px='xs'
                        py='xs'
                        borderRadius='xs'
                        backgroundColor={isHovered ? 'grey02' : 'transparent'}>
                        <Icon
                          name='Trash2'
                          variant='m'
                          color={isHovered ? 'alertRed' : 'grey05'}
                        />
                      </Box>
                    )}
                  </SimpleHoverable>
                </TooltipWebV3>
              </Box>
            </Box>
          )}
        </HoverableMemberRow>
      );
    },
    [
      formatDate,
      getRoleDisplay,
      handleResendInvitation,
      handleDeleteInvitation,
      canManageInvitations,
      t,
    ]
  );

  // Component to render skills with exact logic from ProjectMemberRow
  const MemberSkillsRenderer: React.FC<{
    skills: User['skills'];
    userId: string;
  }> = ({ skills, userId }) => {
    const [skillWidth, setSkillWidth] = useState(0);
    const [skillWidth1, setSkillWidth1] = useState(0);
    const [skillWidth2, setSkillWidth2] = useState(0);
    const [skillWidth3, setSkillWidth3] = useState(0);
    const [skillWidth4, setSkillWidth4] = useState(0);
    const [skillWidth5, setSkillWidth5] = useState(0);
    const [skillWidth6, setSkillWidth6] = useState(0);
    const [skillWidth7, setSkillWidth7] = useState(0);
    const [skillWidth8, setSkillWidth8] = useState(0);
    const [skillWidth9, setSkillWidth9] = useState(0);
    const [skillWidth10, setSkillWidth10] = useState(0);
    const [skillWidth100, setSkillWidth100] = useState(0);

    const renderSkills = () => {
      if (!skills || skills.length === 0) return null;

      const getSkillsWidth = (index: number) => {
        switch (index) {
          case 0:
            return theme.spacing.xxs;
          case 1:
            return skillWidth1 + theme.spacing.xxs;
          case 2:
            return skillWidth1 + skillWidth2 + theme.spacing.xxs * index;
          case 3:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              theme.spacing.xxs * index
            );
          case 4:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              theme.spacing.xxs * index
            );
          case 5:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              skillWidth5 +
              theme.spacing.xxs * index
            );
          case 6:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              skillWidth5 +
              skillWidth6 +
              theme.spacing.xxs * index
            );
          case 7:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              skillWidth5 +
              skillWidth6 +
              skillWidth7 +
              theme.spacing.xxs * index
            );
          case 8:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              skillWidth5 +
              skillWidth6 +
              skillWidth7 +
              skillWidth8 +
              theme.spacing.xxs * index
            );
          case 9:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              skillWidth5 +
              skillWidth6 +
              skillWidth7 +
              skillWidth8 +
              skillWidth9 +
              theme.spacing.xxs * index
            );
          case 10:
            return (
              skillWidth1 +
              skillWidth2 +
              skillWidth3 +
              skillWidth4 +
              skillWidth5 +
              skillWidth6 +
              skillWidth7 +
              skillWidth8 +
              skillWidth9 +
              skillWidth10 +
              theme.spacing.xxs * index
            );
          default:
            return 0;
        }
      };

      const getSkillMaxWidth = (index: number) => {
        return skills.length === index + 1
          ? getSkillMaxWidthNotHaveEllipsis(index)
          : getSkillMaxWidthHaveEllipsis(index);
      };

      const getSkillMaxWidthNotHaveEllipsis = (index: number) => {
        return (
          skillWidth -
          getSkillsWidth(index) +
          (index > 0 ? theme.spacing.xxs : 0)
        );
      };

      const getSkillMaxWidthHaveEllipsis = (index: number) => {
        return (
          skillWidth - getSkillsWidth(index) - skillWidth100 - theme.spacing.xs
        );
      };

      const getSkillBox = (index: number, needSetWidth: boolean) => {
        let setSkillWidthFn: (value: React.SetStateAction<number>) => void;
        switch (index) {
          case 0:
            setSkillWidthFn = setSkillWidth1;
            break;
          case 1:
            setSkillWidthFn = setSkillWidth2;
            break;
          case 2:
            setSkillWidthFn = setSkillWidth3;
            break;
          case 3:
            setSkillWidthFn = setSkillWidth4;
            break;
          case 4:
            setSkillWidthFn = setSkillWidth5;
            break;
          case 5:
            setSkillWidthFn = setSkillWidth6;
            break;
          case 6:
            setSkillWidthFn = setSkillWidth7;
            break;
          case 7:
            setSkillWidthFn = setSkillWidth8;
            break;
          case 8:
            setSkillWidthFn = setSkillWidth9;
            break;
          case 9:
            setSkillWidthFn = setSkillWidth10;
            break;
        }
        return (
          <Box
            key={userId.toString() + '_' + skills[index].id}
            marginTop='xxs'
            marginRight='xxs'
            padding='xxs'
            borderRadius='xxs'
            backgroundColor='grey01'
            maxWidth={getSkillMaxWidth(index)}
            onLayout={(e) =>
              needSetWidth &&
              setSkillWidthFn &&
              setSkillWidthFn(e.nativeEvent.layout.width)
            }>
            <Text
              variant='bodyTaskName'
              color='textPrimary'
              numberOfLines={1}
              style={{
                overflow: 'hidden',
              }}>
              {skills[index].name}
            </Text>
          </Box>
        );
      };

      const getSkillsBox = (
        skillsNum: number,
        haveEllipsis: boolean,
        needSetWidth: boolean
      ) => {
        const newSkills = [];
        for (let i = 0; i < skillsNum; i++) {
          newSkills.push(getSkillBox(i, needSetWidth));
        }

        const skill100Box = haveEllipsis ? (
          <BootstrapTooltip
            key={userId + '_' + skills[skillsNum].id}
            title={
              <PopupHiddenSkills
                showHiddenSkillsFirstIndex={skillsNum}
                skills={skills as unknown as []}
                oneSkillPerLine
              />
            }>
            <Box
              zIndex={10}
              marginTop='xxs'
              marginRight='xxs'
              padding='xxs'
              borderRadius='xxs'
              backgroundColor='grey01'
              onLayout={(e) => {
                setSkillWidth100(e.nativeEvent.layout.width);
              }}>
              <Text
                variant='bodyTaskName'
                color='textPrimary'
                numberOfLines={1}>
                ...
              </Text>
            </Box>
          </BootstrapTooltip>
        ) : undefined;

        return [newSkills, skill100Box];
      };

      for (let i = 1; i <= skills.length; i++) {
        if (getSkillMaxWidthNotHaveEllipsis(i) > skillWidth100 * 2) {
          if (skills.length > i) {
            if (getSkillMaxWidthHaveEllipsis(i) <= theme.spacing.xxs) {
              return getSkillsBox(i, true, false);
            } else {
              continue;
            }
          }
          return getSkillsBox(i, false, true);
        }
        return getSkillsBox(i - 1 > 0 ? i - 1 : 1, skills.length > 1, true);
      }
    };

    return (
      <Box
        flex={1}
        flexDirection='row'
        justifyContent='flex-start'
        flexWrap='wrap'
        onLayout={(e) => setSkillWidth(e.nativeEvent.layout.width)}>
        {renderSkills()}
      </Box>
    );
  };

  // Render active member item
  const renderActiveMemberItem = useCallback(
    (item: User, isTheLastOne: boolean) => {
      const role = getUserRole(item);
      const roleDisplay = getRoleDisplay(role);
      const showRoleDropdown = canEditMemberRole(item);
      const currentUserRole = getCurrentUserRole();
      const hasRoleEditCapability =
        currentUserRole === TeamRole.Owner ||
        currentUserRole === TeamRole.Admin;

      return (
        <HoverableMemberRow isTheLastOne={isTheLastOne}>
          {/* Name Column */}
          <Box flex={2.4} flexDirection='row' alignItems='center'>
            <Box
              onStartShouldSetResponder={() => true}
              onResponderTerminationRequest={() => false}>
              {/* @ts-expect-error - Avatar component has incorrect return type definition */}
              <Avatar
                id={item.id}
                avatar={item.avatar}
                label={
                  item.name ||
                  `${item.firstName || ''} ${item.lastName || ''}`.trim()
                }
                size='small'
                disableAvatarPress={false}
              />
            </Box>
            <Text
              variant='webBodySecondary'
              color='textPrimary'
              marginLeft='xs'
              numberOfLines={1}>
              {item.name ||
                `${item.firstName || ''} ${item.lastName || ''}`.trim()}
            </Text>
          </Box>

          {/* Skills Column */}
          <Box
            mx='m'
            flex={2.4}
            flexDirection='row'
            justifyContent='flex-start'
            alignItems='center'>
            {item.skills && item.skills.length > 0 ? (
              <MemberSkillsRenderer skills={item.skills} userId={item.id} />
            ) : null}
          </Box>

          {/* Email Column */}
          <Box flex={2} flexDirection='row' justifyContent='flex-start' mr='s'>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              numberOfLines={1}>
              {item.email || ''}
            </Text>
          </Box>

          {/* Phone Column */}
          <Box
            flex={1.98}
            alignItems='flex-end'
            justifyContent='flex-end'
            mx='m'>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              numberOfLines={1}>
              {item.phoneNumber ? formatPhoneNumber(item.phoneNumber) : ''}
            </Text>
          </Box>

          {/* Role Column */}
          <Box
            ml='m'
            flex={1.4}
            flexDirection='row'
            justifyContent='flex-start'
            paddingLeft='l'
            alignItems='center'>
            {showRoleDropdown ? (
              <BaseButtonV3
                noBorder={true}
                noRadius={false}
                noHover={false}
                buttonHeight={36}
                buttonRadius='xxs'
                mr='none'
                modalRadius={8}
                showHoverWhenDropdown={true}
                hoverBackgroundColor='grey02'
                restingBackgroundColor='transparent'
                popModalComponent={({ onClose }) => (
                  <TeamMemberRoleSelector
                    onRoleSelect={(newRole) => {
                      handleRoleSelect(item.id, newRole);
                      onClose();
                    }}
                  />
                )}>
                <Box
                  width={118}
                  height={36}
                  px='m'
                  py='xs'
                  alignItems='center'
                  justifyContent='space-between'
                  flexDirection='row'>
                  <Text
                    variant='bodyTaskName'
                    color='textPrimary'
                    marginRight='s'
                    flex={1}>
                    {roleDisplay}
                  </Text>
                  <Icon
                    name='ChevronDown'
                    variant='xs'
                    color='grey05'
                    style={{ marginTop: 2 }}
                  />
                </Box>
              </BaseButtonV3>
            ) : (
              <Box
                paddingVertical='xs'
                paddingHorizontal={hasRoleEditCapability ? 'm' : 'none'}
                borderRadius='xxs'>
                <Text
                  variant='bodyTaskName'
                  color='textPrimary'
                  marginRight='xxs'>
                  {roleDisplay}
                </Text>
              </Box>
            )}
          </Box>

          {/* Chat Icon Column */}
          <Box
            flex={0.64}
            alignItems='center'
            justifyContent='center'
            onStartShouldSetResponder={() => true}
            onResponderTerminationRequest={() => false}>
            <ChatIconWithTooltip
              onPress={() => {
                createChat({
                  variables: {
                    attributes: {
                      userIds: [item.id],
                    },
                  },
                });
              }}
              accessibilityLabel={t('shared:chatWithMember', {
                name:
                  item.name ||
                  `${item.firstName || ''} ${item.lastName || ''}`.trim() ||
                  t('shared:unknownUser'),
              })}
            />
          </Box>

          {/* Delete Icon Column — always reserve space when user has manage capability;
              only render the interactive icon when this specific member can be removed */}
          {hasRoleEditCapability && (
            <Box flex={0.64} alignItems='center' justifyContent='center'>
              {canEditMemberRole(item) && (
                <SimpleHoverable
                  onPress={() => handleRoleSelect(item.id, 'remove')}
                  accessibilityLabel={t('shared:removeMember')}>
                  {(isHovered) => (
                    <Box
                      px='xs'
                      py='xs'
                      borderRadius='xs'
                      backgroundColor={isHovered ? 'grey02' : 'transparent'}>
                      <Icon
                        name='Trash2'
                        variant='m'
                        color={isHovered ? 'alertRed' : 'grey05'}
                      />
                    </Box>
                  )}
                </SimpleHoverable>
              )}
            </Box>
          )}
        </HoverableMemberRow>
      );
    },
    [
      createChat,
      getUserRole,
      getRoleDisplay,
      handleRoleSelect,
      canEditMemberRole,
      getCurrentUserRole,
      t,
    ]
  );

  // Render item - handles both User and PendingInvitation
  const renderItem = useCallback(
    (item: User | PendingInvitation, isTheLastOne: boolean) => {
      // Check if it's a PendingInvitation by checking for 'invitedBy' and 'dateSent' properties
      if ('invitedBy' in item && 'dateSent' in item) {
        return renderPendingInvitationItem(
          item as PendingInvitation,
          isTheLastOne
        );
      }
      // Otherwise it's a User
      return renderActiveMemberItem(item as User, isTheLastOne);
    },
    [renderPendingInvitationItem, renderActiveMemberItem]
  );

  // Sort users with owners always on top
  const sortUsers = useCallback(
    (users: User[]): User[] => {
      // Always separate owners and non-owners to ensure owners are on top
      const owners: User[] = [];
      const nonOwners: User[] = [];

      users.forEach((user) => {
        if (getUserRole(user) === TeamRole.Owner) {
          owners.push(user);
        } else {
          nonOwners.push(user);
        }
      });

      // Sort non-owners by name (only if sorting is enabled)
      const sortedNonOwners = isSortName
        ? [...nonOwners].sort((a, b) => {
            const nameA = (
              a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim()
            ).toLowerCase();
            const nameB = (
              b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim()
            ).toLowerCase();

            if (isSortNameAsc) {
              return nameA.localeCompare(nameB);
            } else {
              return nameB.localeCompare(nameA);
            }
          })
        : nonOwners;

      // Sort owners by name as well (only if sorting is enabled)
      const sortedOwners = isSortName
        ? [...owners].sort((a, b) => {
            const nameA = (
              a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim()
            ).toLowerCase();
            const nameB = (
              b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim()
            ).toLowerCase();

            if (isSortNameAsc) {
              return nameA.localeCompare(nameB);
            } else {
              return nameB.localeCompare(nameA);
            }
          })
        : owners;

      // Always return owners first, then non-owners
      return [...sortedOwners, ...sortedNonOwners];
    },
    [isSortName, isSortNameAsc, getUserRole]
  );

  // Filter users based on filter criteria
  const filteredUsers = useMemo(() => {
    if (!team?.users) return [];
    const users = team.users as User[];
    let filtered = users;

    // Apply role filter - Owner is always included
    if (filterRole === 'admin') {
      filtered = filtered.filter(
        (user) =>
          getUserRole(user) === TeamRole.Admin ||
          getUserRole(user) === TeamRole.Owner
      );
    } else if (filterRole === 'member') {
      filtered = filtered.filter(
        (user) =>
          getUserRole(user) === TeamRole.Member ||
          getUserRole(user) === TeamRole.Owner
      );
    }

    // Apply search filter
    if (filterSearch.trim()) {
      const searchLower = filterSearch.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const name =
          user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return (
          name.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phoneNumber?.includes(searchLower)
        );
      });
    }

    return filtered;
  }, [team?.users, filterRole, filterSearch, getUserRole]);

  // Filter pending invitations based on filter criteria
  const filteredPendingInvitations = useMemo(() => {
    let filtered = pendingInvitations;

    // Apply role filter
    if (filterRole === 'admin') {
      filtered = filtered.filter(
        (invitation) => invitation.role === TeamRole.Admin
      );
    } else if (filterRole === 'member') {
      filtered = filtered.filter(
        (invitation) => invitation.role === TeamRole.Member
      );
    }

    // Apply search filter
    if (filterSearch.trim()) {
      const searchLower = filterSearch.toLowerCase().trim();
      filtered = filtered.filter((invitation) => {
        const userName =
          invitation.user?.firstName ||
          invitation.user?.lastName ||
          invitation.name ||
          '';
        return (
          invitation.email?.toLowerCase().includes(searchLower) ||
          invitation.invitedBy?.toLowerCase().includes(searchLower) ||
          userName.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [pendingInvitations, filterRole, filterSearch]);

  // Sort filtered pending invitations by date sent (only if sorting is enabled)
  const sortedFilteredPendingInvitations = useMemo(() => {
    if (!isSortPendingDate) {
      return filteredPendingInvitations;
    }
    return [...filteredPendingInvitations].sort((a, b) => {
      if (isSortPendingDateAsc) {
        return a.dateSent.getTime() - b.dateSent.getTime();
      } else {
        return b.dateSent.getTime() - a.dateSent.getTime();
      }
    });
  }, [filteredPendingInvitations, isSortPendingDate, isSortPendingDateAsc]);

  const sectionDataList = useMemo(() => {
    if (!grouped) return [];
    const sortedUsers = sortUsers(filteredUsers);
    let sections = [];
    sections.push({
      sectionHeader: {
        id: 'ACTIVE' as MemberGroup,
        name: t('projects.members.active'),
        count: sortedUsers.length,
      },
      data: sortedUsers,
    });

    if (team?.id && !team?.personal) {
      sections.push({
        sectionHeader: {
          id: 'PENDING' as MemberGroup,
          name: t('projects.members.pending'),
          count: sortedFilteredPendingInvitations.length,
        },
        data: sortedFilteredPendingInvitations,
      });
    }
    return sections;
  }, [
    grouped,
    filteredUsers,
    sortUsers,
    sortedFilteredPendingInvitations,
    team?.id,
    team?.personal,
    t,
  ]);

  // Get all team members for message member overlay
  const allTeamMembers = useMemo(() => {
    if (!team?.users) return [];
    return team.users as User[];
  }, [team?.users]);

  // Filter members for message member overlay
  const filteredMembersForChat = useMemo(() => {
    if (!messageMemberSearch.trim()) return allTeamMembers;
    const searchLower = messageMemberSearch.toLowerCase().trim();
    return allTeamMembers.filter((user) => {
      const name =
        user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return name.toLowerCase().includes(searchLower);
    });
  }, [allTeamMembers, messageMemberSearch]);

  // Handle filter overlay outside click
  const handleFilterOutsideClick = useCallback((event: MouseEvent) => {
    const isClickInOverlay =
      filterOverlayRef.current &&
      filterOverlayRef.current.contains(event.target as Node);
    const isClickInButton =
      filterButtonRef.current &&
      filterButtonRef.current.contains(event.target as Node);
    if (!isClickInOverlay && !isClickInButton) {
      setShowFilterOverlay(false);
    }
  }, []);

  // Handle message member overlay outside click
  const handleMessageMemberOutsideClick = useCallback((event: MouseEvent) => {
    const isClickInOverlay =
      messageMemberOverlayRef.current &&
      messageMemberOverlayRef.current.contains(event.target as Node);
    const isClickInButton =
      messageMemberButtonRef.current &&
      messageMemberButtonRef.current.contains(event.target as Node);

    if (!isClickInOverlay && !isClickInButton) {
      setShowMessageMemberOverlay(false);
    }
  }, []);

  const filterOutsideClickRef = useOutsideClick<HTMLDivElement>(
    handleFilterOutsideClick
  );
  const messageMemberOutsideClickRef = useOutsideClick<HTMLDivElement>(
    handleMessageMemberOutsideClick
  );

  // Handle create chat with selected members
  const handleCreateChat = useCallback(() => {
    if (selectedMembersForChat.length === 0) return;

    // Exclude current user from chat participants
    const membersToChat = me
      ? selectedMembersForChat.filter((id) => id !== me.id)
      : selectedMembersForChat;

    // Check the count after filtering out current user
    if (membersToChat.length === 1) {
      // Single person: create normal chat
      createChat({
        variables: {
          attributes: {
            userIds: membersToChat,
          },
        },
      });
    } else {
      // Multiple people: create group chat
      // Generate group name from first names of chat participants
      const groupName =
        allTeamMembers
          .filter((user) => membersToChat.includes(user.id))
          .map((user) => user.firstName || user.name || '')
          .filter((name) => name.trim() !== '')
          .join(', ') || t('shared:group');

      createChat({
        variables: {
          attributes: {
            name: groupName,
            userIds: membersToChat,
            groupChat: true,
          },
        },
      });
    }

    setShowMessageMemberOverlay(false);
    setSelectedMembersForChat([]);
    setMessageMemberSearch('');
  }, [selectedMembersForChat, allTeamMembers, createChat, me, t]);

  // Handle clear all filters
  const handleClearFilters = useCallback(() => {
    setFilterSearch('');
    setFilterRole(null);
  }, []);

  // Calculate filter count
  const filterCount = useMemo(() => {
    let count = 0;
    // Role filter (admin or member) counts as 1
    if (filterRole) {
      count += 1;
    }
    // Search filter counts as 1
    if (filterSearch.trim()) {
      count += 1;
    }
    return count;
  }, [filterRole, filterSearch]);

  // Toggle member selection for chat
  const toggleMemberSelection = useCallback((userId: string) => {
    setSelectedMembersForChat((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  }, []);

  // Toggle all members selection
  const toggleAllMembersSelection = useCallback(() => {
    // Exclude current user (owner) from selectable members
    const selectableMembers = me
      ? filteredMembersForChat.filter((m) => m.id !== me.id)
      : filteredMembersForChat;
    const allFilteredMemberIds = selectableMembers.map((m) => m.id);
    const allSelected = allFilteredMemberIds.every((id) =>
      selectedMembersForChat.includes(id)
    );

    if (allSelected) {
      // Deselect all filtered members
      setSelectedMembersForChat((prev) =>
        prev.filter((id) => !allFilteredMemberIds.includes(id))
      );
    } else {
      // Select all filtered members (excluding current user)
      setSelectedMembersForChat((prev) => {
        const newSelection = [...prev];
        allFilteredMemberIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  }, [filteredMembersForChat, selectedMembersForChat, me]);

  // Get filter overlay position
  const getFilterOverlayPosition = useCallback(() => {
    if (!filterButtonRef.current) return undefined;
    const rect = filterButtonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left,
    };
  }, []);

  // Get message member overlay position
  const getMessageMemberOverlayPosition = useCallback(() => {
    if (!messageMemberButtonRef.current) return undefined;
    const rect = messageMemberButtonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left,
    };
  }, []);

  const adminConfirmationMessage = (
    <Box>
      <Text variant='bodySecondary' marginBottom='xs'>
        {t('shared:adminRoleConfirmBody')}
      </Text>
      <Box marginLeft='m' marginTop='xs'>
        <Text variant='bodySecondary' marginBottom='xxs'>
          {`\u2022 ${t('shared:adminAbilityManage')}`}
        </Text>
        <Text variant='bodySecondary' marginBottom='xxs'>
          {`\u2022 ${t('shared:adminAbilityInvite')}`}
        </Text>
        <Text variant='bodySecondary' marginBottom='xxs'>
          {`\u2022 ${t('shared:adminAbilityUpload')}`}
        </Text>
        <Text variant='bodySecondary'>
          {`\u2022 ${t('shared:adminAbilityTrack')}`}
        </Text>
      </Box>
    </Box>
  );

  const renderSectionHeader = useCallback(
    (item: Header) => <MembersSectionHeader item={item} />,
    []
  );

  const inviteButton = useCallback(
    (isShowUpgradeTeam: boolean) => {
      return (
        <Button
          width={160}
          height={32}
          variant='primary'
          backgroundColor='black'
          color='white'
          isSmall
          onPress={() => {
            if (isShowUpgradeTeam) {
              setPaywallTeam(team);
              setPaywallModalOpen(true);
            } else {
              setIsInviteModalVisible(true);
            }
          }}
          accessibilityLabel={t('shared:inviteMembers')}
          prefix={<Icon name='UserPlus' width={16} height={16} color='white' />}
          prefixMarginRight='xs'>
          <Text variant='webSmall' color='white'>
            {t('shared:inviteMembers')}
          </Text>
        </Button>
      );
    },
    [team, t, setPaywallTeam, setPaywallModalOpen, setIsInviteModalVisible]
  );

  const renderEmptyComponent = useCallback(
    (sectionId: string) => {
      if (sectionId === 'PENDING') {
        const isPendingLoading = pendingRequestInitialLoading || teamLoading;
        if (isPendingLoading) {
          // TA-10280: replaces the bare spinner with row skeletons matching
          // the real pending-invitation table, same as every other list in
          // this rollout.
          return (
            <SkeletonList
              row={(index) => (
                <PendingInvitationRowSkeleton
                  key={index}
                  showActionColumn={canManageInvitations()}
                />
              )}
            />
          );
        }
        return (
          <Box paddingHorizontal='m' paddingBottom='xl' alignItems='center'>
            <Box
              width={64}
              height={64}
              justifyContent='center'
              alignItems='center'
              borderRadius='xxl'
              backgroundColor='grey01'
              marginTop='xs'>
              <Icon name='Mail' color='grey04' width={28} height={28} />
            </Box>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              textAlign='center'
              marginTop='xs'
              marginBottom='xs'>
              {t('teams.noPendingMembers')}
            </Text>
            {inviteButton(shouldShowPaywall(team))}
          </Box>
        );
      }
      // TA-10280: the active-members table had no loading state at all —
      // an empty section (the state before the team query resolves) fell
      // straight through to "No members found." Skeleton it like every
      // other list in this rollout, scoped to "still loading" so a
      // genuinely empty team still gets the real empty message.
      if (teamLoading) {
        return (
          <SkeletonList row={(index) => <MemberRowSkeleton key={index} />} />
        );
      }
      return (
        <Box paddingVertical='s' paddingHorizontal='m'>
          <Text variant='body' color='textSecondary' textAlign='center'>
            {t('shared:noMembersFound')}
          </Text>
        </Box>
      );
    },
    [
      pendingRequestInitialLoading,
      teamLoading,
      t,
      inviteButton,
      canManageInvitations,
    ]
  );

  const membersListContent = useMemo(
    () => (
      <Box width='100%' flex={1} style={styles.mainListContainer}>
        <CollapsableSectionListV3<Header, User | PendingInvitation>
          itemHeight={56}
          contentBottomMargin='none'
          data={sectionDataList}
          renderSectionHeader={renderSectionHeader}
          renderSectionContentHeader={renderTableHeader}
          stickySectionContentHeader={true}
          accordionMode={false}
          initialExpandedSectionIds={INITIAL_EXPANDED_SECTION_IDS}
          ListHeaderComponent={ListHeaderComponent}
          renderItem={renderItem}
          renderNewTaskItem={renderNewTaskItem}
          disableContextMenu={true}
          renderEmptyComponent={renderEmptyComponent}
          showEmptyMessage={true}
          onRightMenuAction={noopRightMenuAction}
        />
      </Box>
    ),
    [
      sectionDataList,
      renderSectionHeader,
      renderTableHeader,
      renderItem,
      renderEmptyComponent,
    ]
  );

  return (
    <Box marginTop='xxs' flex={1} style={styles.mainListContainer}>
      {/* Filter and Message Member Buttons */}
      <Box
        flexDirection='row'
        alignItems='center'
        marginBottom='m'
        paddingHorizontal='xs'>
        <Box ref={filterButtonRef} marginRight='xs' flexDirection='row'>
          <SimpleHoverable
            onPress={() => setShowFilterOverlay(!showFilterOverlay)}
            accessibilityLabel={t('shared:filterMembers')}>
            {(isHovered) => (
              <Box
                flexDirection='row'
                alignItems='center'
                paddingHorizontal='m'
                borderRadius='xs'
                maxHeight={36}
                height={36}
                marginLeft='xs'
                backgroundColor={
                  isHovered || showFilterOverlay || filterCount > 0
                    ? 'grey01'
                    : 'white'
                }>
                <Icon name='AlignCenterDesc' variant='s' color='textPrimary' />
                <Text variant='webSmall' color='textPrimary' marginLeft='xs'>
                  {t('shared:filter')}
                </Text>
                {filterCount > 0 && (
                  <>
                    <Box
                      marginLeft='xs'
                      width={20}
                      height={20}
                      justifyContent='center'
                      backgroundColor='greenSecondary'
                      borderRadius='xl'>
                      <Text
                        variant='metadata'
                        color='white'
                        textAlign='center'
                        mr='xxxs'>
                        {filterCount}
                      </Text>
                    </Box>
                    <TouchableOpacity
                      style={{ marginLeft: theme.spacing.xs }}
                      onPress={handleClearFilters}
                      accessibilityLabel={t('shared:clearFilters')}>
                      <Box alignItems='center' justifyContent='center'>
                        <Icon name='Close' variant='xxxs' color='textPrimary' />
                      </Box>
                    </TouchableOpacity>
                  </>
                )}
              </Box>
            )}
          </SimpleHoverable>
        </Box>

        <Box ref={messageMemberButtonRef}>
          <SimpleHoverable
            onPress={() =>
              setShowMessageMemberOverlay(!showMessageMemberOverlay)
            }
            accessibilityLabel={t('shared:startChat')}>
            {(isHovered) => (
              <Box
                flexDirection='row'
                alignItems='center'
                paddingHorizontal='m'
                borderRadius='xs'
                maxHeight={36}
                height={36}
                backgroundColor={
                  isHovered || showMessageMemberOverlay ? 'grey01' : 'white'
                }>
                <Icon name='MessageSquare' variant='s' color='textPrimary' />
                <Text variant='webSmall' color='textPrimary' marginLeft='xs'>
                  {t('shared:startChat')}
                </Text>
              </Box>
            )}
          </SimpleHoverable>
        </Box>
      </Box>

      {showBanner && (
        <TeamNoMembersBanner
          onInviteMembers={() => {
            if (shouldShowPaywall(team)) {
              setPaywallTeam(team);
              setPaywallModalOpen(true);
            } else {
              setIsInviteModalVisible(true);
            }
          }}
          onDismiss={() => {
            setBannerDismissed(true);
          }}
        />
      )}
      <InviteMemberModal
        isVisible={isInviteModalVisible}
        onClose={() => setIsInviteModalVisible(false)}
        teamId={team?.id || ''}
      />

      {/* Filter Overlay */}
      <MembersFilterOverlay
        visible={showFilterOverlay}
        searchValue={filterSearch}
        roleOptions={teamMemberFilterOptions}
        selectedRole={filterRole}
        onClose={() => setShowFilterOverlay(false)}
        onSearchChange={setFilterSearch}
        onRoleChange={(role) => setFilterRole(role)}
        onClearAll={handleClearFilters}
        overlayRef={filterOverlayRef}
        outsideClickRef={filterOutsideClickRef}
        position={getFilterOverlayPosition()}
      />

      {/* Message Member Overlay */}
      <MessageMemberOverlay
        visible={showMessageMemberOverlay}
        onClose={() => setShowMessageMemberOverlay(false)}
        searchValue={messageMemberSearch}
        onSearchChange={setMessageMemberSearch}
        filteredMembers={filteredMembersForChat}
        selectedMembers={selectedMembersForChat}
        onToggleMemberSelection={toggleMemberSelection}
        onToggleAllMembersSelection={toggleAllMembersSelection}
        onCreateChat={handleCreateChat}
        overlayRef={messageMemberOverlayRef}
        outsideClickRef={messageMemberOutsideClickRef}
        position={getMessageMemberOverlayPosition()}
      />

      {membersListContent}
      <ConfirmModal
        showModal={showConfirmModal}
        isAlert={true}
        onClose={handleCancelRoleChange}
        onPress={handleConfirmRoleChange}
        buttonText={t('shared:confirm')}
        title={t('shared:updateRole')}
        message={adminConfirmationMessage}
      />
      {pendingMemberToRemove && (
        <ConfirmModal
          showModal={showRemoveConfirmModal}
          onClose={handleCancelRemoveMember}
          onPress={handleConfirmRemoveMember}
          buttonText={t('shared:remove')}
          isAlert={true}
          title={t('shared:removeMember')}
          isLoading={isRemovingMember}
          message={
            <Text variant='bodySecondary'>
              <Text variant='bodySecondary' fontWeight='bold'>
                {pendingMemberToRemove.name ||
                  `${pendingMemberToRemove.firstName || ''} ${
                    pendingMemberToRemove.lastName || ''
                  }`.trim()}
              </Text>{' '}
              {t('shared:removeMemberConfirmBody')}
            </Text>
          }
        />
      )}
      {pendingInvitationToDelete && (
        <ConfirmModal
          showModal={showDeleteInvitationModal}
          onClose={handleCancelDeleteInvitation}
          onPress={handleConfirmDeleteInvitation}
          buttonText={t('shared:cancelInvite')}
          isAlert={true}
          title={t('shared:cancelInvite')}
          isLoading={isDeletingInvitation}
          message={
            <Text variant='bodySecondary'>
              {t('shared:cancelInvitationTo', {
                email: pendingInvitationToDelete.email,
              })}
            </Text>
          }
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  mainListContainer: {
    flex: 1,
    minHeight: 0,
  },
});
